import { useAtom } from 'jotai';
import { demoModeAtom, userAtom } from '@/global-store';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarInset,
  SidebarProvider,
} from '@layout/Sidebar/Sidebar';
import { Link as RouterLink, Outlet, useNavigation } from 'react-router-dom';
import { MessageCircleQuestion, MessageSquareText } from 'lucide-react';
import { useFetchAccessibilityData } from '@http/queries/useFetchAccessibilityData';
import MenuList from './Sidebar/MenuList/MenuList';
import Header from '@layout/Header/Header';
import { Button, useTailwindBreakpoint } from '@eye-able/ui';
import Footer from '@layout/Footer';
import LoadingSpinner from '@components/LoadingSpinner';
import { usePathname } from '@hooks/usePathname';
import { cn } from '@helpers/cn';
import { isInternalTeamUser } from '@helpers/isInternalTeamUser';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation.tsx';
import { FloatingDemoOfferActions } from './FloatingDemoOfferActions';
import AnnouncementBar from './AnnouncementBar';
import { useState } from 'react';
import { useSidebar } from '@layout/Sidebar/SidebarContext';

export function MainLayout() {
  const xlAndUp = useTailwindBreakpoint('xl');

  return (
    <SidebarProvider
      className='eye:flex-col'
      style={{
        ['--app-header-content-height']: `${xlAndUp ? 75 : 52}px`,
      }}
    >
      <MainLayoutContent />
    </SidebarProvider>
  );
}

// Separate component so it can read the sidebar context that MainLayout provides.
function MainLayoutContent() {
  const [demoMode] = useAtom(demoModeAtom);
  const [user] = useAtom(userAtom);
  const { isMobile, openMobile } = useSidebar();

  const shouldShowDemoActions = demoMode && isMobile && !openMobile;

  const { t } = useInlineTranslation();

  const path = usePathname();

  const isReportArea = path.startsWith('/report');
  const isRulesArea = path.startsWith('/rulesOverview');

  const { isLoading } = useFetchAccessibilityData();

  const navigation = useNavigation();
  const [announcementBarHeight, setAnnouncementBarHeight] = useState(0);

  const isPageLoading =
    navigation.state === 'loading' ||
    ((isReportArea || isRulesArea) && isLoading);

  const renderMainContent = () => {
    if (isPageLoading) {
      return <LoadingSpinner />;
    }
    return <Outlet />;
  };

  const shouldShowForInternalFeedback = isInternalTeamUser(user);

  return (
    <div
      className='eye:flex eye:flex-col eye:min-h-svh eye:w-full'
      style={{
        ['--app-announcement-height']: `${announcementBarHeight}px`,
        ['--app-header-height']:
          'calc(var(--app-announcement-height) + var(--app-header-content-height))',
      }}
    >
      <AnnouncementBar onHeightChange={setAnnouncementBarHeight} />
      <Header />
      <div aria-hidden style={{ height: 'var(--app-header-height)' }} />
      <div
        className='eye:bg-grey-10 eye:flex eye:w-full eye:flex-1'
        style={{ paddingRight: 'var(--removed-body-scroll-bar-size, 0px)' }}
      >
        <Sidebar
          className='no-print'
          collapsible='icon'
          side='left'
          variant='inset'
        >
          <SidebarContent
            className='eye:bg-white'
            aria-label={t('mainNavigation')}
          >
            <MenuList />
          </SidebarContent>
          {!demoMode && (
            <SidebarFooter>
              <ul className='eye:flex eye:flex-col eye:gap-xs'>
                {shouldShowForInternalFeedback && (
                  <li>
                    <Button className='eye:w-full' variant='outline' asChild>
                      <a
                        href='https://form.asana.com/?k=UFeLGkl2Ortj6cEohzwSrA&d=1199244943732308'
                        target='_blank'
                        rel='noopener noreferrer'
                      >
                        <MessageSquareText aria-hidden />
                        {t('productFeedback')}
                      </a>
                    </Button>
                  </li>
                )}
                <li>
                  <div id='support-popup'>
                    <Button className='eye:w-full' variant='outline' asChild>
                      <RouterLink to='/help-center'>
                        <MessageCircleQuestion aria-hidden />
                        {t('helpCenter')}
                      </RouterLink>
                    </Button>
                  </div>
                </li>
              </ul>
            </SidebarFooter>
          )}
        </Sidebar>

        <div
          className={cn(
            'eye:min-w-0 eye:max-w-full',
            'eye:px-16 eye:pt-32 eye:sm:px-6 eye:sm:py-6 eye:lg:px-48 eye:lg:py-[40px]',
            'eye:flex eye:flex-col eye:flex-1 eye:flex-nowrap',
            'eye:overflow-x-clip eye:wrap-break-word',
            shouldShowDemoActions ? 'eye:pb-[100px]!' : 'eye:pb-32',
          )}
        >
          <SidebarInset className='eye:w-full eye:max-w-[1500px] eye:mx-auto'>
            <main className='eye:relative eye:flex eye:w-full eye:flex-1 eye:flex-col'>
              {renderMainContent()}
            </main>
            {/* Outside <main>, not inside it: a contentinfo landmark nested in
                main is not exposed as the page's footer. */}
            <Footer />
          </SidebarInset>
        </div>
      </div>

      {shouldShowDemoActions ? <FloatingDemoOfferActions /> : null}
    </div>
  );
}
